import React, { useEffect, useMemo, useState } from 'react';
import { IPersonalRating } from '../../interfaces';
import RatingDialogLayout, { ratingCardId } from '../common/RatingDialogLayout';
import RatingPaneHeader from '../common/RatingPaneHeader';
import RatingRail, { IRatingRailItem } from '../common/RatingRail';
import { formatRatingPeriod, pluralize } from '../common/ratingUtils';
import { useRatingToast } from '../common/useRatingToast';
import EmployeeCard from './EmployeeCard';
import {
  answeredCount,
  answerOf,
  buildPersonalRequests,
  firstUnanswered,
  groupPersonalRatings,
  IEmployee,
  IPersonalPartnerGroup,
  PersonalRatingState,
  remainingCount,
  restoreAnswer,
  setComment,
  setRating,
  skippedCount,
  toggleSkip,
} from './personalRatingState';
import { useSavePersonalRating } from './useSavePersonalRating';

const RATING_FORMS: [string, string, string] = ['оценка', 'оценки', 'оценок'];
const EMPLOYEE_FORMS: [string, string, string] = ['сотрудник', 'сотрудника', 'сотрудников'];
const RATED_FORMS: [string, string, string] = ['сотруднику', 'сотрудникам', 'сотрудникам'];

const employeesText = (count: number) => `${count} ${pluralize(count, EMPLOYEE_FORMS)}`;

const railItem = (group: IPersonalPartnerGroup, state: PersonalRatingState): IRatingRailItem => ({
  key: group.key,
  title: group.partnerName,
  subtitle: [group.serviceTypeNames.join(', '), employeesText(group.employees.length)].join(' · '),
  chips: group.realEstateNames,
  filled: answeredCount(group, state),
  total: group.employees.length,
});

const partnerMeta = (group: IPersonalPartnerGroup) =>
  [
    group.serviceTypeNames.join(', '),
    employeesText(group.employees.length),
    `работает на ${group.realEstateNames.length} ЖК`,
  ].join(' · ');

interface IProps {
  open: boolean;
  personal: IPersonalRating[];
  onClose(): void;
  onSaveRating(): void;
}

const SavePersonalRatingDialog = ({ open, personal, onClose, onSaveRating }: IProps) => {
  const groups = useMemo(() => groupPersonalRatings(personal), [personal]);
  const [state, setState] = useState<PersonalRatingState>({});
  const [current, setCurrent] = useState(0);
  const [sentList, setSentList] = useState<IPersonalRating[] | null>(null);
  const [flashKey, setFlashKey] = useState<string | null>(null);
  const [lastRatedKey, setLastRatedKey] = useState<string | null>(null);
  const [openRealEstates, setOpenRealEstates] = useState<Record<string, boolean>>({});
  const { saveRating, loading } = useSavePersonalRating();
  const toast = useRatingToast();

  const index = Math.min(current, Math.max(groups.length - 1, 0));
  const group = groups[index];
  const remaining = remainingCount(groups, state);
  const period = formatRatingPeriod(personal[0]?.dateAt);

  useEffect(() => {
    if (flashKey) document.getElementById(ratingCardId(flashKey))?.scrollIntoView({ block: 'center' });
  }, [flashKey, index]);

  const select = (next: number) => {
    setCurrent(Math.max(0, Math.min(groups.length - 1, next)));
    setFlashKey(null);
  };

  const handleRate = (key: string, rating: number) => {
    setState((prev) => setRating(prev, key, rating));
    setLastRatedKey(key);
    setFlashKey(null);
  };

  const handleSkip = (employee: IEmployee) => {
    const previous = answerOf(state, employee.key);
    setState((prev) => toggleSkip(prev, employee.key));
    setFlashKey(null);
    if (previous.skipped) return;
    const undo =
      previous.rating === null ? undefined : () => setState((prev) => restoreAnswer(prev, employee.key, previous));
    toast(`${employee.fullName} пропущен: оценка за период не выставляется и не влияет на рейтинг.`, undo);
  };

  const handleCommentBlur = (key: string) => {
    if (!answerOf(state, key).comment.trim() && lastRatedKey === key) setLastRatedKey(null);
  };

  const showComment = (key: string) => {
    const answer = answerOf(state, key);
    return !answer.skipped && answer.rating !== null && (lastRatedKey === key || answer.comment.trim() !== '');
  };

  const toggleRealEstates = (key: string) => setOpenRealEstates((prev) => ({ ...prev, [key]: !prev[key] }));

  const handleSubmit = async () => {
    const missing = firstUnanswered(groups, state);
    if (missing) {
      setCurrent(missing.groupIndex);
      setFlashKey(missing.key);
      toast(`Не заполнено ${remaining} ${pluralize(remaining, RATING_FORMS)}. Перешли к первому незаполненному.`);
      return;
    }
    const saved = await saveRating(buildPersonalRequests(groups, state));
    if (saved) {
      setSentList(personal);
      const skipped = skippedCount(groups, state);
      const rated = groups.reduce((acc, item) => acc + item.employees.length, 0) - skipped;
      const skippedText = skipped > 0 ? `, ${skipped} пропущено` : '';
      toast(`Отправлено: оценки по ${rated} ${pluralize(rated, RATED_FORMS)}${skippedText}. Период: ${period}.`);
    }
    // После ошибки тоже перезапрашиваем: строку мог оценить другой пользователь
    onSaveRating();
  };

  if (!group) return null;

  return (
    <RatingDialogLayout
      open={open}
      title="Оценка персонала"
      subtitle={`Период: ${period} · сотрудники сгруппированы по партнеру, оценка ставится один раз по каждому сотруднику`}
      rail={<RatingRail items={groups.map((item) => railItem(item, state))} activeIndex={index} onSelect={select} />}
      footerText="Сотрудник оценивается один раз, оценка действует на всех объектах его закрепления. «Не могу оценить» — сотрудник пропускается за период, оценка не выставляется."
      submitLabel="Оценить сотрудников"
      remaining={remaining}
      loading={loading || sentList === personal}
      onSubmit={handleSubmit}
      onClose={onClose}
    >
      <RatingPaneHeader
        index={index}
        total={groups.length}
        title={group.partnerName}
        meta={partnerMeta(group)}
        onPrev={() => select(index - 1)}
        onNext={() => select(index + 1)}
      />

      {group.employees.map((employee) => (
        <EmployeeCard
          key={employee.key}
          id={ratingCardId(employee.key)}
          employee={employee}
          answer={answerOf(state, employee.key)}
          flash={flashKey === employee.key}
          showComment={showComment(employee.key)}
          realEstatesOpen={Boolean(openRealEstates[employee.key])}
          onToggleRealEstates={() => toggleRealEstates(employee.key)}
          onRate={(rating) => handleRate(employee.key, rating)}
          onToggleSkip={() => handleSkip(employee)}
          onComment={(comment) => setState((prev) => setComment(prev, employee.key, comment))}
          onCommentFocus={() => setLastRatedKey(employee.key)}
          onCommentBlur={() => handleCommentBlur(employee.key)}
        />
      ))}
    </RatingDialogLayout>
  );
};

export default SavePersonalRatingDialog;
